import {
  normalizeManuscriptContent,
} from "./childrensBookManuscriptContent";


function clean(
  value
) {
  return String(
    value ?? ""
  ).trim();
}


function safeArray(
  value
) {
  return Array.isArray(
    value
  )
    ? value
    : [];
}


function failure(
  code,
  error
) {
  return {
    ok: false,
    code,
    error,
  };
}


function blockText(
  block = {}
) {
  return safeArray(
    block?.runs
  )
    .map(
      (run) =>
        typeof run?.text ===
          "string"
          ? run.text
          : ""
    )
    .join(
      ""
    );
}


function continuationIdentity(
  requestedId
) {
  const blockId =
    clean(
      requestedId
    );

  const match =
    blockId.match(
      /^(.*)-part-(\d+)$/
    );

  if (
    match &&
    clean(
      match[1]
    ) &&
    Number(
      match[2]
    ) >= 2
  ) {
    return {
      blockId,
      rootId:
        clean(
          match[1]
        ),
      fragmentNumber:
        Number(
          match[2]
        ),
    };
  }

  return {
    blockId,
    rootId:
      blockId,
    fragmentNumber: 1,
  };
}


function orderedTargetBlocks({
  orderedUnitIds,
  targetEntries,
}) {
  const entriesByUnitId =
    new Map(
      safeArray(
        targetEntries
      ).map(
        (entry) => [
          clean(
            entry?.unitId
          ),
          entry,
        ]
      )
    );

  const blocks = [];

  for (
    const requestedUnitId of safeArray(
      orderedUnitIds
    )
  ) {
    const unitId =
      clean(
        requestedUnitId
      );

    const entry =
      entriesByUnitId.get(
        unitId
      );

    if (!entry) {
      continue;
    }

    const normalized =
      normalizeManuscriptContent(
        entry.manuscriptContent
      );

    if (
      !normalized.ok ||
      !normalized.content
    ) {
      return failure(
        "target_manuscript_invalid",
        "A flowed manuscript page contains invalid structured content."
      );
    }

    for (
      const block of normalized
        .content
        .blocks
    ) {
      blocks.push({
        unitId,
        block,
      });
    }
  }

  return {
    ok: true,
    blocks,
  };
}


function continuationFragmentsFrom(
  blocks,
  sourceBlockId
) {
  const sourceIdentity =
    continuationIdentity(
      sourceBlockId
    );

  const startIndex =
    blocks.findIndex(
      (item) =>
        clean(
          item?.block?.id
        ) ===
        sourceIdentity.blockId
    );

  if (startIndex < 0) {
    return failure(
      "selection_block_not_found",
      "The edited manuscript block is no longer present after local flow."
    );
  }

  const fragments = [];
  let expectedFragment =
    sourceIdentity
      .fragmentNumber;

  for (
    let index =
      startIndex;
    index <
      blocks.length;
    index += 1
  ) {
    const item =
      blocks[
        index
      ];

    if (
      item?.block?.type ===
        "page_break"
    ) {
      continue;
    }

    const identity =
      continuationIdentity(
        item?.block?.id
      );

    if (
      identity.rootId !==
        sourceIdentity.rootId ||
      identity.fragmentNumber !==
        expectedFragment
    ) {
      break;
    }

    fragments.push({
      ...item,
      identity,
      text:
        blockText(
          item.block
        ),
    });

    expectedFragment += 1;
  }

  if (!fragments.length) {
    return failure(
      "selection_lineage_not_found",
      "The edited manuscript position cannot be located safely after local flow."
    );
  }

  return {
    ok: true,
    fragments,
  };
}


function mappedEndpoint(
  fragments,
  requestedOffset
) {
  let remaining =
    Math.max(
      0,
      Number(
        requestedOffset
      ) || 0
    );

  for (
    let index = 0;
    index <
      fragments.length;
    index += 1
  ) {
    const fragment =
      fragments[
        index
      ];

    const textLength =
      fragment
        .text
        .length;

    if (
      remaining <=
        textLength
    ) {
      return {
        unitId:
          fragment.unitId,
        blockId:
          clean(
            fragment
              .block
              ?.id
          ),
        offset:
          remaining,
      };
    }

    remaining -=
      textLength;

    if (
      index <
      fragments.length - 1
    ) {
      const nextText =
        fragments[
          index + 1
        ]?.text || "";

      const boundaryWhitespaceIsMaterialized =
        /\s$/.test(
          fragment.text
        ) ||
        /^\s/.test(
          nextText
        );

      if (
        !boundaryWhitespaceIsMaterialized
      ) {
        remaining =
          Math.max(
            0,
            remaining - 1
          );
      }
    }
  }

  const last =
    fragments[
      fragments.length - 1
    ];

  return {
    unitId:
      last.unitId,
    blockId:
      clean(
        last.block?.id
      ),
    offset:
      last.text.length,
  };
}


/*
 * Pure cursor/selection handoff for a manuscript edit that has already
 * been redistributed across existing pages.
 *
 * This carries no persistent manuscript authority. It maps only the
 * editor's ephemeral same-block selection coordinates onto the
 * continuation fragments produced by automatic flow.
 *
 * When a selected range would span two physical pages, both endpoints
 * are reported but restorableSelection is null. The live editor may
 * therefore fail closed instead of inventing a cross-page DOM selection.
 */
export function mapChildrensBookManuscriptSelectionThroughReflow({
  orderedUnitIds = [],
  targetEntries = [],
  selection = {},
} = {}) {
  const blockId =
    clean(
      selection?.blockId
    );

  if (!blockId) {
    return failure(
      "selection_block_required",
      "A manuscript block is required for cursor handoff."
    );
  }

  const ordered =
    orderedTargetBlocks({
      orderedUnitIds,
      targetEntries,
    });

  if (!ordered.ok) {
    return ordered;
  }

  const lineage =
    continuationFragmentsFrom(
      ordered.blocks,
      blockId
    );

  if (!lineage.ok) {
    return lineage;
  }

  const start =
    mappedEndpoint(
      lineage.fragments,
      selection?.start
    );

  const end =
    mappedEndpoint(
      lineage.fragments,
      selection?.end
    );

  const sameUnit =
    start.unitId ===
      end.unitId;

  return {
    ok: true,

    collapsed:
      Number(
        selection?.start
      ) ===
      Number(
        selection?.end
      ),

    start,
    end,

    focusUnitId:
      end.unitId,

    restorableSelection:
      sameUnit
        ? {
            unitId:
              end.unitId,

            selection: {
              blockId:
                end.blockId,
              start:
                start.offset,
              end:
                end.offset,
            },
          }
        : null,
  };
}
